import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { StackedSidebarService } from './stacked-sidebar.service';
import { SidebarType } from '../components/layout/stacked-sidebar/stacked-sidebar.interface';
import type { CompanyFormResult } from '../components/board-v3/companies/company-form/company-form.component';
import type { CompanyDetailResult } from '../components/board-v3/companies/company-detail/company-detail.component';
import {
  BoardCompaniesPage,
  BoardCompany,
  BoardFilterSet,
  CreateCompanyPayload,
  GetCompaniesParams,
  SaveBoardFilterPayload,
  UpdateCompanyPayload,
} from '../components/board-v3/board-v3.types';

/**
 * Client companies of the CRM (`company_v2` on the backend, `company` is the tenant there),
 * so their ids are called `companyV2Id` here
 */
@Injectable({
  providedIn: 'root',
})
export class BoardCompanyService {
  private readonly http = inject(HttpClient);
  private readonly stackedSidebarService = inject(StackedSidebarService);
  private readonly url = environment.baseURL + '/board/company';

  public getCompanies(params: GetCompaniesParams = {}): Observable<BoardCompaniesPage> {
    const query: Record<string, string | number> = {};
    if (params.filters?.length) query['filters'] = JSON.stringify(params.filters);
    if (params.filter_mode) query['filter_mode'] = params.filter_mode;
    if (params.page) query['page'] = params.page;
    if (params.take) query['take'] = params.take;

    return this.http
      .get<{ data: BoardCompaniesPage }>(this.url, { params: query })
      .pipe(map((res) => res.data ?? { items: [], total: 0, size: 0, page: 1 }));
  }

  public getFilters(): Observable<BoardFilterSet[]> {
    return this.http.get<{ data: BoardFilterSet[] }>(`${this.url}/filters`).pipe(map(res => res.data));
  }

  public createFilter(payload: SaveBoardFilterPayload): Observable<BoardFilterSet> {
    return this.http.post<{ data: BoardFilterSet }>(`${this.url}/filters`, payload).pipe(map(res => res.data));
  }

  public updateFilter(id: string, payload: SaveBoardFilterPayload): Observable<BoardFilterSet> {
    return this.http.put<{ data: BoardFilterSet }>(`${this.url}/filters/${id}`, payload).pipe(map(res => res.data));
  }

  public deleteFilter(id: string): Observable<void> {
    return this.http.delete<void>(`${this.url}/filters/${id}`);
  }

  public getCompany(companyV2Id: string): Observable<BoardCompany> {
    return this.http.get<{ data: BoardCompany }>(`${this.url}/${companyV2Id}`).pipe(map((res) => res.data));
  }

  public createCompany(payload: CreateCompanyPayload): Observable<BoardCompany> {
    return this.http.post<{ data: BoardCompany }>(this.url, payload).pipe(map((res) => res.data));
  }

  /** Only the sent fields change; `phones` / `emails` / `addresses` replace the whole list */
  public updateCompany(companyV2Id: string, payload: UpdateCompanyPayload): Observable<BoardCompany> {
    return this.http
      .put<{ data: BoardCompany }>(`${this.url}/${companyV2Id}`, payload)
      .pipe(map((res) => res.data));
  }

  public deleteCompany(companyV2Id: string): Observable<void> {
    return this.http.delete(`${this.url}/${companyV2Id}`).pipe(map(() => undefined));
  }

  /** Already linked cards are skipped, a card that has another company gets this one instead */
  public linkCards(companyV2Id: string, cardIds: string[]): Observable<BoardCompany> {
    return this.http
      .post<{ data: BoardCompany }>(`${this.url}/${companyV2Id}/card`, { card_ids: cardIds })
      .pipe(map((res) => res.data));
  }

  public unlinkCard(companyV2Id: string, cardId: string): Observable<BoardCompany> {
    return this.http
      .delete<{ data: BoardCompany }>(`${this.url}/${companyV2Id}/card/${cardId}`)
      .pipe(map((res) => res.data));
  }

  // #region sidebars

  /** Creates a company, linked to `cardIds` right away when given */
  public openCompanyForm(cardIds: string[] = []): Observable<CompanyFormResult | undefined> {
    return this.stackedSidebarService.openSidebar(
      { type: SidebarType.BoardV3CompanyForm, width: '440px' },
      { cardIds },
    );
  }

  /**
   * `canOpenCards` must be off inside a card detail: a nested card detail would replace
   * the shared `BoardService.card` of the outer one
   */
  public openCompanyDetail(companyV2Id: string, canOpenCards = true): Observable<CompanyDetailResult | undefined> {
    return this.stackedSidebarService.openSidebar(
      { type: SidebarType.BoardV3CompanyDetail, width: '90vw' },
      { companyV2Id, canOpenCards },
    );
  }

  // #endregion
}
